import { InfoCircleOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Flex, Space, VFlex } from '@/components/base';
import FlexibleTextarea from '@/components/Inputs/FlexibleTextarea';
import Input from '@/components/Inputs/Input';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Typo, { Paragraph, Span } from '@/components/Typography/Typo';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import { radius } from '@/styles/sizes';
import {
  type FEEDBACK_COLUMNS,
  FeedbackColumnNameMap,
} from '@/types/filterKeyConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

type Props = {
  title: string;
  desc?: string;
  saved_rule?: SavedRule;
  editable?: {
    onTitleChange: (title: string) => void;
    onDescChange: (desc: string) => void;
  };
};

const StaticSearchFilterDisplay = (props: Props) => {
  const { saved_rule } = props;

  const { parseStructuredFilter } = useStructuredFilter();
  const [state, setState] = useState<{
    title: string;
    desc?: string;
    dateRange: [Dayjs, Dayjs];
    dataFilter: StructuredFilterQueryFormatT[];
    keywordFilter?: StructuredFilterStringNumberValueSet | null;
    aiKeywordFilter?: string;
  }>({
    title: props.title,
    desc: props.desc,
    dateRange: [getTimezoneDayjs(), getTimezoneDayjs()],
    dataFilter: [],
    keywordFilter: null,
    aiKeywordFilter: saved_rule?.query,
  });

  const initializeFilter = useCallback(async () => {
    const sFilter = new SFilter(saved_rule?.filter);

    const dateRangeFilter = sFilter.getDateFilter();

    const dateRange: [Dayjs, Dayjs] = [
      getTimezoneDayjs(dateRangeFilter?.start_date || undefined),
      getTimezoneDayjs(dateRangeFilter?.end_date || undefined),
    ];

    const keywordFilter = sFilter.getSearchKeywordFilter();

    const filterMap = sFilter.getDataFilterMap();

    const dataFilter = parseStructuredFilter(filterMap);

    setState({
      title: props.title,
      desc: props.desc,
      dateRange: dateRange,
      keywordFilter: keywordFilter,
      aiKeywordFilter: saved_rule?.query,
      dataFilter: dataFilter,
    });
  }, [
    parseStructuredFilter,
    props.desc,
    props.title,
    saved_rule?.filter,
    saved_rule?.query,
  ]);

  useEffect(() => {
    initializeFilter();
  }, [initializeFilter]);

  const keywordValue = useMemo(() => {
    if (!state?.keywordFilter) return '-';
    const result = structuredFilterUtils.getStaticKeywordFilterString(
      state.keywordFilter,
    );
    if (!result) return '-';
    const { operatorString, valueString } = result;
    return (
      <Typo>
        <Typo className="text-gray-600">{operatorString}</Typo>
        &nbsp;&nbsp;
        <Typo>{valueString}</Typo>
      </Typo>
    );
  }, [state?.keywordFilter]);

  const dataFilterValue = useMemo(() => {
    if (!state?.dataFilter) return '-';
    if (state.dataFilter.length === 0) return '-';
    return state.dataFilter.map((filter, index, array) => {
      const columnName = FeedbackColumnNameMap.get(
        filter.columnKey as FEEDBACK_COLUMNS,
      );
      return (
        <Typo key={index}>
          <Typo>{columnName}</Typo>
          &nbsp;
          <Typo className="text-gray-600">
            {structuredFilterUtils.getOperatorTextMap({
              operator: filter.operator,
              isMultiValueField: true,
              hasMultiValues: filter.filterValues.length > 1,
            })}
          </Typo>
          &nbsp;
          <Typo>{filter.filterValues.map((v) => v.filterName).join(', ')}</Typo>
          <br />
        </Typo>
      );
    });
  }, [state?.dataFilter]);

  const dateString = state?.dateRange[0].format(DATE_FORMAT.readable) || '-';

  return (
    <TopicInfoWrapper padding="24px">
      {props.editable ? (
        <>
          <VFlex g={10}>
            <FilterLabel>Name</FilterLabel>
            <Input
              placeholder="Enter topic name"
              sizeType="l"
              value={state.title}
              onChange={(e) => {
                props.editable?.onTitleChange(e.currentTarget.value);
                setState({ ...state, title: e.currentTarget.value });
              }}
            />
          </VFlex>
          <Space h={32} />
          <VFlex g={10}>
            <FilterLabel>Description</FilterLabel>
            <FlexibleTextarea
              placeholder="Optional"
              value={state?.desc || ''}
              onChange={(e) => {
                props.editable?.onDescChange(e.currentTarget.value);
                setState({ ...state, desc: e.currentTarget.value });
              }}
              minHeight={100}
              style={{
                maxHeight: 200,
              }}
            />
          </VFlex>
          <Space h={32} />
        </>
      ) : (
        <>
          <FilterItemDisplay
            label="Title"
            value={<FilterValue>{state.title || '-'}</FilterValue>}
          />
          <Space h={20} />
          <FilterItemDisplay
            label="Description"
            value={<FilterValue>{state.desc || '-'}</FilterValue>}
          />
          <Space h={20} />
        </>
      )}

      <VFlex g={20}>
        <FilterItemDisplay
          label="Prompt"
          value={<FilterValue>{state.aiKeywordFilter || '-'}</FilterValue>}
        />
        <FilterItemDisplay
          label="Keywords"
          value={<FilterValue>{keywordValue || '-'}</FilterValue>}
        />
        <FilterItemDisplay
          label="Tracking begun on"
          tooltip="Beginning date for this topic tracking."
          value={<FilterValue>{dateString || '-'}</FilterValue>}
        />
        <FilterItemDisplay
          label="Filters"
          value={<FilterValue>{dataFilterValue}</FilterValue>}
        />
      </VFlex>
    </TopicInfoWrapper>
  );
};

const TopicInfoWrapper = styled(VFlex)`
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: ${radius.card}px;
`;

const FilterItemDisplay = (props: {
  label: string;
  value: ReactNode;
  tooltip?: ReactNode;
}) => {
  return (
    <Flex g={4}>
      <FilterLabel>
        {props.label}
        {props.tooltip && (
          <TooltipV2 tooltipNode={props.tooltip}>
            <InfoCircleOutlined style={{ paddingLeft: 4 }} />
          </TooltipV2>
        )}
      </FilterLabel>
      {props.value}
    </Flex>
  );
};

const FilterLabel = styled(Span).attrs({
  size: 2,
})`
  color: ${(props) => props.theme.colors.gray600};
  width: 200px;
`;

const FilterValue = styled(Paragraph).attrs({
  size: 2,
})`
  flex: 1;
  color: ${(props) => props.theme.colors.gray800};
`;

export default StaticSearchFilterDisplay;
